import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Switch from '@material-ui/core/Switch';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import { makeStyles } from '@material-ui/core/styles';
import { isRidgeLightBand } from '../../../DataStructures/roofWindowTypes';
import { ConfigurationContext } from '../../../ContextProviders/ConfigurationProvider';
import useConfigurationChanges from '../../../Hooks/useConfigurationChanges';
import DialogContentDivider from '../../DialogContentDivider';
import GroupLabel from '../../GroupLabel';
import { useSelector } from 'react-redux';
import { findByEmbeddedOptionIdentifier, getEmbeddedOptionIdentifier } from '../../../Selectors/Option';
import useItemRules from '../../../Rules/useItemRules';
import { isPVCWall } from '../../../DataStructures/wallTypes';
import ConfirmDialog from '../../../ConfirmDialog';

const useStyles = makeStyles(() => ({
  icon: {
    width: 35,
    height: 30,
    display: 'inline-flex',
    color: '#ED1C29',
    opacity: 0.38,
  },
  labelWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export const Roof = () => {
  const configuration = useContext(ConfigurationContext);
  const { setRoofType, setRoofWindowType, setRainGutter } = configuration;
  const {
    roofType,
    roofWindowType,
    rainGutter,
    isRoofWindowEnabled,
    isRidgeLightBandEnabled,
  } = useConfigurationChanges(configuration);
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    localOptions: { roof: roofTypes, roof_window: roofWindowTypes, rain_gutter: rainGutterTypes },
  } = useSelector((state) => state.Option);

  const rules = useItemRules();

  const [confirmState, setConfirmState] = useState({ show: false, value: null });

  const onClose = () => setConfirmState({ show: false });
  const onConfirm = () => {
    setRoofType(findByEmbeddedOptionIdentifier(roofTypes, confirmState.value));
    setConfirmState({ show: false, value: null });
  };

  const onChange = ({ target: { value } }) => {
    if (isPVCWall(configuration.getData().wallType) && value !== 'roof_type_pvc_with_gable') {
      setConfirmState({ show: true, value });
      return;
    }
    setRoofType(findByEmbeddedOptionIdentifier(roofTypes, value));
  };

  return (
    <>
      <ConfirmDialog
        open={confirmState.show}
        content={t('pvcHallDialog.invalidRoofType')}
        onConfirm={onConfirm}
        onClose={onClose}
        confirmButtonLabel={t('pvcHallDialog.confirm')}
      />
      <GroupLabel>{t('roofType')}</GroupLabel>
      <RadioGroup aria-label="roof type" value={getEmbeddedOptionIdentifier(roofType)} onChange={onChange}>
        {roofTypes.map(({ option: { identifier, title } }) => (
          <FormControlLabel key={identifier} value={identifier} control={<Radio />} label={title} />
        ))}
      </RadioGroup>

      {!!roofWindowTypes.length && (
        <>
          <DialogContentDivider />

          <GroupLabel>{t('roofWindow')}</GroupLabel>
          <RadioGroup
            aria-label="roof window type"
            value={getEmbeddedOptionIdentifier(roofWindowType)}
            onChange={({ target: { value } }) =>
              setRoofWindowType(findByEmbeddedOptionIdentifier(roofWindowTypes, value))
            }
            disabled={!isRoofWindowEnabled}
          >
            {roofWindowTypes.map(({ value, option: { identifier, title } }) => (
              <div className={classes.labelWrapper} key={`labelWrapper${identifier}`}>
                <FormControlLabel
                  key={identifier}
                  value={identifier}
                  control={<Radio />}
                  label={title}
                  disabled={
                    !isRoofWindowEnabled || (isRidgeLightBand(value) ? !isRidgeLightBandEnabled : false)
                  }
                />
                {(!isRoofWindowEnabled || (isRidgeLightBand(value) ? !isRidgeLightBandEnabled : false)) && (
                  <ErrorOutline className={classes.icon} />
                )}
              </div>
            ))}
          </RadioGroup>
        </>
      )}

      {!!rainGutterTypes.length && (
        <>
          <DialogContentDivider />

          <FormControlLabel
            disabled={rules.rainGutter?.disabled}
            control={
              <Switch
                checked={rainGutter}
                onChange={(evt) => setRainGutter(evt.target.checked)}
                name="rain gutter"
              />
            }
            label={t('rainGutter')}
          />
        </>
      )}
    </>
  );
};

export default Roof;
